import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, BarChart3, Building, Calendar, DollarSign, 
  AlertTriangle, Download, RefreshCw, Monitor, TrendingUp,
  Shield, Clock, UserCheck, UserMinus, Wifi, WifiOff, 
  RotateCcw, AlertCircle, PieChart, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';

const AssetReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [endpoints, setEndpoints] = useState([]);
  const [assetMeta, setAssetMeta] = useState({});
  const [reports, setReports] = useState({
    // IT Reports
    byDepartment: [],
    needsRefresh: [],
    warrantyExpiring: [],
    criticalUpdates: [],
    needsReboot: [],
    offlineDevices: [],
    // Finance Reports
    budgetSummary: { total: 0, byYear: {}, byDepartment: {} },
    depreciation: [],
    checkoutStatus: { checkedOut: 0, available: 0, byPerson: [] },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [endpointsRes, metaRes] = await Promise.all([
        api.getAction1Endpoints({ limit: 200 }),
        api.getAssetMetadata()
      ]);
      
      const eps = endpointsRes.data.endpoints || [];
      setEndpoints(eps);
      
      // Build metadata lookup by endpoint_id AND endpoint_name
      const meta = {};
      const metaByName = {};
      (metaRes.data.assets || []).forEach(a => {
        if (a.endpoint_id) meta[a.endpoint_id] = a;
        if (a.endpoint_name) metaByName[a.endpoint_name.toLowerCase()] = a;
      });
      setAssetMeta({ ...meta, ...metaByName });
      
      // Generate reports
      generateReports(eps, meta, metaByName);
      
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const getMeta = (ep, metaById, metaByName) => {
    return metaById[ep.id] || metaByName[ep.name?.toLowerCase()] || {};
  };

  const generateReports = (eps, metaById, metaByName) => {
    const now = new Date();
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    
    // === IT REPORTS ===
    
    // By Department
    const deptCounts = {};
    const deptCosts = {};
    eps.forEach(ep => {
      const m = getMeta(ep, metaById, metaByName);
      const dept = m.department || 'Unassigned';
      deptCounts[dept] = (deptCounts[dept] || 0) + 1;
      if (m.cost) {
        deptCosts[dept] = (deptCosts[dept] || 0) + m.cost;
      }
    });
    
    const byDepartment = Object.entries(deptCounts)
      .map(([name, count]) => ({ 
        name, 
        count, 
        cost: deptCosts[name] || 0 
      }))
      .sort((a, b) => b.count - a.count);
    
    // Needs Refresh (3+ years)
    const needsRefresh = [];
    eps.forEach(ep => {
      const m = getMeta(ep, metaById, metaByName);
      if (m.purchase_date) {
        const purchaseDate = new Date(m.purchase_date);
        if (purchaseDate < threeYearsAgo) {
          const ageYears = Math.floor((now - purchaseDate) / (365.25 * 24 * 60 * 60 * 1000));
          needsRefresh.push({
            ...ep,
            meta: m,
            ageYears,
            purchaseDate: m.purchase_date
          });
        }
      }
    });
    needsRefresh.sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));
    
    // Warranty Expiring (next 3 months)
    const warrantyExpiring = [];
    eps.forEach(ep => {
      const m = getMeta(ep, metaById, metaByName);
      if (m.warranty_expiration) {
        const warrantyDate = new Date(m.warranty_expiration);
        if (warrantyDate > now && warrantyDate < threeMonthsFromNow) {
          const daysLeft = Math.ceil((warrantyDate - now) / (24 * 60 * 60 * 1000));
          warrantyExpiring.push({
            ...ep,
            meta: m,
            warrantyDate: m.warranty_expiration,
            daysLeft
          });
        }
      }
    });
    warrantyExpiring.sort((a, b) => a.daysLeft - b.daysLeft);
    
    // Critical Updates Needed
    const criticalUpdates = eps.filter(ep => 
      (ep.missing_updates?.critical || 0) > 0
    ).sort((a, b) => (b.missing_updates?.critical || 0) - (a.missing_updates?.critical || 0));
    
    // Needs Reboot
    const needsReboot = eps.filter(ep => ep.reboot_required === 'Yes');
    
    // Offline Devices
    const offlineDevices = eps.filter(ep => 
      ep.status === 'Disconnected' || ep.online_status !== 'SUCCESS'
    );
    
    // === FINANCE REPORTS ===
    
    // Budget Summary
    let totalSpent = 0;
    const byYear = {};
    const byDept = {};
    
    // Combine all metadata
    const allMeta = { ...metaById };
    Object.entries(metaByName).forEach(([name, m]) => {
      if (!Object.values(allMeta).find(v => v.endpoint_name?.toLowerCase() === name)) {
        allMeta[name] = m;
      }
    });
    
    Object.values(allMeta).forEach(m => {
      if (m.cost) {
        totalSpent += m.cost;
        
        // By year
        if (m.purchase_date) {
          const year = m.purchase_date.split('-')[0];
          byYear[year] = (byYear[year] || 0) + m.cost;
        }
        
        // By department
        const dept = m.department || 'Unassigned';
        byDept[dept] = (byDept[dept] || 0) + m.cost;
      }
    });
    
    // Depreciation Report (5-year straight line)
    const depreciation = [];
    eps.forEach(ep => {
      const m = getMeta(ep, metaById, metaByName);
      if (m.cost && m.purchase_date) {
        const purchaseDate = new Date(m.purchase_date);
        const ageYears = (now - purchaseDate) / (365.25 * 24 * 60 * 60 * 1000);
        const depreciationRate = 0.20; // 20% per year (5-year life)
        const accumulatedDep = Math.min(m.cost, m.cost * depreciationRate * ageYears);
        const bookValue = Math.max(0, m.cost - accumulatedDep);
        
        depreciation.push({
          ...ep,
          meta: m,
          originalCost: m.cost,
          ageYears: Math.round(ageYears * 10) / 10,
          accumulatedDepreciation: accumulatedDep,
          bookValue
        });
      }
    });
    depreciation.sort((a, b) => b.originalCost - a.originalCost);
    
    // Checkout Status
    let checkedOut = 0;
    let available = 0;
    const byPerson = {};
    
    Object.values(allMeta).forEach(m => {
      if (m.checkout_status === 'checked_out') {
        checkedOut++;
        const person = m.current_assignee || 'Unknown';
        byPerson[person] = (byPerson[person] || 0) + 1;
      } else {
        available++;
      }
    });
    
    const checkoutByPerson = Object.entries(byPerson)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    
    setReports({
      byDepartment,
      needsRefresh,
      warrantyExpiring,
      criticalUpdates,
      needsReboot,
      offlineDevices,
      budgetSummary: {
        total: totalSpent,
        byYear: Object.entries(byYear).sort((a, b) => b[0] - a[0]),
        byDepartment: Object.entries(byDept).sort((a, b) => b[1] - a[1])
      },
      depreciation,
      checkoutStatus: { 
        checkedOut, 
        available, 
        byPerson: checkoutByPerson 
      }
    });
  };

  const exportCSV = (data, filename, headers) => {
    const rows = data.map(row => headers.map(h => row[h.key] ?? '').join(','));
    const csv = [headers.map(h => h.label).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const exportRefreshReport = () => {
    const data = reports.needsRefresh.map(ep => ({
      device: ep.name,
      serial: ep.serial,
      user: ep.user?.split('\\').pop() || '',
      department: ep.meta?.department || '',
      purchaseDate: ep.purchaseDate,
      ageYears: ep.ageYears,
      cost: ep.meta?.cost || ''
    }));
    exportCSV(data, 'devices_needing_refresh', [
      { key: 'device', label: 'Device' },
      { key: 'serial', label: 'Serial' },
      { key: 'user', label: 'User' },
      { key: 'department', label: 'Department' },
      { key: 'purchaseDate', label: 'Purchase Date' },
      { key: 'ageYears', label: 'Age (Years)' },
      { key: 'cost', label: 'Cost' }
    ]);
  };

  const exportDepreciationReport = () => {
    const data = reports.depreciation.map(ep => ({
      device: ep.name,
      serial: ep.serial,
      department: ep.meta?.department || '',
      purchaseDate: ep.meta?.purchase_date || '',
      originalCost: ep.originalCost?.toFixed(2),
      ageYears: ep.ageYears,
      accumulatedDep: ep.accumulatedDepreciation?.toFixed(2),
      bookValue: ep.bookValue?.toFixed(2)
    }));
    exportCSV(data, 'asset_depreciation', [
      { key: 'device', label: 'Device' },
      { key: 'serial', label: 'Serial' },
      { key: 'department', label: 'Department' },
      { key: 'purchaseDate', label: 'Purchase Date' },
      { key: 'originalCost', label: 'Original Cost' },
      { key: 'ageYears', label: 'Age (Years)' },
      { key: 'accumulatedDep', label: 'Accumulated Depreciation' },
      { key: 'bookValue', label: 'Book Value' }
    ]);
  };

  const exportWarrantyReport = () => {
    const data = reports.warrantyExpiring.map(ep => ({
      device: ep.name,
      serial: ep.serial,
      user: ep.user?.split('\\').pop() || '',
      department: ep.meta?.department || '',
      warrantyDate: ep.warrantyDate,
      daysLeft: ep.daysLeft,
      vendor: ep.meta?.vendor || ''
    }));
    exportCSV(data, 'warranty_expiring', [
      { key: 'device', label: 'Device' },
      { key: 'serial', label: 'Serial' },
      { key: 'user', label: 'User' },
      { key: 'department', label: 'Department' },
      { key: 'warrantyDate', label: 'Warranty Expires' },
      { key: 'daysLeft', label: 'Days Left' },
      { key: 'vendor', label: 'Vendor' }
    ]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const totalBookValue = reports.depreciation.reduce((sum, ep) => sum + ep.bookValue, 0);
  const totalOriginalCost = reports.depreciation.reduce((sum, ep) => sum + ep.originalCost, 0);
  const totalDepreciation = reports.depreciation.reduce((sum, ep) => sum + ep.accumulatedDepreciation, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/assets')} className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h1 className="font-bold text-slate-800">Asset Reports</h1>
                <p className="text-xs text-slate-400">{endpoints.length} devices in Action1</p>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </header>

      <div className="p-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <Wifi className="h-4 w-4" />
                <span className="text-xs font-medium">Online</span>
              </div>
              <p className="text-2xl font-bold text-green-700">{endpoints.length - reports.offlineDevices.length}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <WifiOff className="h-4 w-4" />
                <span className="text-xs font-medium">Offline</span>
              </div>
              <p className="text-2xl font-bold text-gray-700">{reports.offlineDevices.length}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-orange-600 mb-1">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs font-medium">Needs Refresh</span>
              </div>
              <p className="text-2xl font-bold text-orange-700">{reports.needsRefresh.length}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <Shield className="h-4 w-4" />
                <span className="text-xs font-medium">Critical Updates</span>
              </div>
              <p className="text-2xl font-bold text-red-700">{reports.criticalUpdates.length}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <RotateCcw className="h-4 w-4" />
                <span className="text-xs font-medium">Needs Reboot</span>
              </div>
              <p className="text-2xl font-bold text-blue-700">{reports.needsReboot.length}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-purple-600 mb-1">
                <UserCheck className="h-4 w-4" />
                <span className="text-xs font-medium">Checked Out</span>
              </div>
              <p className="text-2xl font-bold text-purple-700">{reports.checkoutStatus.checkedOut}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for IT vs Finance */}
        <Tabs defaultValue="it" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="it" className="gap-2">
              <Monitor className="h-4 w-4" />
              IT Reports
            </TabsTrigger>
            <TabsTrigger value="finance" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Finance Reports
            </TabsTrigger>
          </TabsList>

          {/* IT Reports Tab */}
          <TabsContent value="it" className="space-y-6">
            {/* Devices by Department */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Devices by Department
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {reports.byDepartment.map(dept => (
                    <div 
                      key={dept.name} 
                      className="p-3 bg-gray-50 rounded-lg text-center cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => navigate(`/assets?department=${encodeURIComponent(dept.name)}`)}
                    >
                      <p className="text-2xl font-bold text-gray-800">{dept.count}</p>
                      <p className="text-xs text-gray-500 truncate">{dept.name}</p>
                      {dept.cost > 0 && (
                        <p className="text-xs text-green-600 mt-1">{formatCurrency(dept.cost)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Critical Updates & Reboot */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Critical Updates */}
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <Shield className="h-5 w-5" />
                    Devices with Critical Updates ({reports.criticalUpdates.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reports.criticalUpdates.length === 0 ? (
                    <p className="text-center py-4 text-gray-500">All devices are up to date! ✅</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {reports.criticalUpdates.slice(0, 15).map(ep => (
                        <div 
                          key={ep.id} 
                          className="flex items-center justify-between p-2 bg-red-50 rounded cursor-pointer hover:bg-red-100"
                          onClick={() => navigate(`/assets/${ep.id}`)}
                        >
                          <div>
                            <p className="font-medium text-sm">{ep.name}</p>
                            <p className="text-xs text-gray-500">{ep.user?.split('\\').pop()}</p>
                          </div>
                          <Badge className="bg-red-100 text-red-700">
                            {ep.missing_updates?.critical} critical
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Needs Reboot */}
              <Card className="border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <RotateCcw className="h-5 w-5" />
                    Devices Needing Reboot ({reports.needsReboot.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reports.needsReboot.length === 0 ? (
                    <p className="text-center py-4 text-gray-500">No devices need rebooting ✅</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {reports.needsReboot.slice(0, 15).map(ep => (
                        <div 
                          key={ep.id} 
                          className="flex items-center justify-between p-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100"
                          onClick={() => navigate(`/assets/${ep.id}`)}
                        >
                          <div>
                            <p className="font-medium text-sm">{ep.name}</p>
                            <p className="text-xs text-gray-500">{ep.user?.split('\\').pop()}</p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-700">
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Reboot
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Warranty Expiring */}
            <Card className="border-yellow-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-yellow-700">
                  <Clock className="h-5 w-5" />
                  Warranty Expiring Soon ({reports.warrantyExpiring.length})
                </CardTitle>
                {reports.warrantyExpiring.length > 0 && (
                  <Button variant="outline" size="sm" onClick={exportWarrantyReport} className="gap-2">
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {reports.warrantyExpiring.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No warranties expiring in the next 3 months</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-yellow-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Device</th>
                          <th className="text-left p-3 font-medium text-gray-600">User</th>
                          <th className="text-left p-3 font-medium text-gray-600">Vendor</th>
                          <th className="text-left p-3 font-medium text-gray-600">Expires</th>
                          <th className="text-left p-3 font-medium text-gray-600">Days Left</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {reports.warrantyExpiring.map(ep => (
                          <tr 
                            key={ep.id} 
                            className="hover:bg-yellow-50 cursor-pointer"
                            onClick={() => navigate(`/assets/${ep.id}`)}
                          >
                            <td className="p-3">
                              <div className="font-medium">{ep.name}</div>
                              <div className="text-xs text-gray-400">{ep.serial}</div>
                            </td>
                            <td className="p-3 text-gray-600">{ep.user?.split('\\').pop() || '-'}</td>
                            <td className="p-3 text-gray-600">{ep.meta?.vendor || '-'}</td>
                            <td className="p-3 text-gray-600">{ep.warrantyDate}</td>
                            <td className="p-3">
                              <Badge className={`text-xs ${ep.daysLeft < 30 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {ep.daysLeft} days
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Devices Needing Refresh */}
            <Card className="border-orange-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <AlertTriangle className="h-5 w-5" />
                  Devices Needing Refresh - 3+ Years ({reports.needsRefresh.length})
                </CardTitle>
                {reports.needsRefresh.length > 0 && (
                  <Button variant="outline" size="sm" onClick={exportRefreshReport} className="gap-2">
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {reports.needsRefresh.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Monitor className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No devices need refresh</p>
                    <p className="text-xs">Add purchase dates to track device age</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Device</th>
                          <th className="text-left p-3 font-medium text-gray-600">User</th>
                          <th className="text-left p-3 font-medium text-gray-600">Department</th>
                          <th className="text-left p-3 font-medium text-gray-600">Purchase Date</th>
                          <th className="text-left p-3 font-medium text-gray-600">Age</th>
                          <th className="text-left p-3 font-medium text-gray-600">Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {reports.needsRefresh.slice(0, 20).map(ep => (
                          <tr 
                            key={ep.id} 
                            className="hover:bg-orange-50 cursor-pointer"
                            onClick={() => navigate(`/assets/${ep.id}`)}
                          >
                            <td className="p-3">
                              <div className="font-medium">{ep.name}</div>
                              <div className="text-xs text-gray-400">{ep.serial}</div>
                            </td>
                            <td className="p-3 text-gray-600">{ep.user?.split('\\').pop() || '-'}</td>
                            <td className="p-3">
                              {ep.meta?.department ? (
                                <Badge variant="outline" className="text-xs">{ep.meta.department}</Badge>
                              ) : '-'}
                            </td>
                            <td className="p-3 text-gray-600">{ep.purchaseDate}</td>
                            <td className="p-3">
                              <Badge className="bg-orange-100 text-orange-700 text-xs">
                                {ep.ageYears} years
                              </Badge>
                            </td>
                            <td className="p-3 text-gray-600">
                              {ep.meta?.cost ? formatCurrency(ep.meta.cost) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="p-3 bg-orange-50 border-t">
                      <p className="text-sm text-orange-700">
                        <strong>{reports.needsRefresh.length}</strong> devices are over 3 years old
                        {reports.needsRefresh.reduce((sum, ep) => sum + (ep.meta?.cost || 0), 0) > 0 && (
                          <span> • Original investment: <strong>
                            {formatCurrency(reports.needsRefresh.reduce((sum, ep) => sum + (ep.meta?.cost || 0), 0))}
                          </strong></span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Finance Reports Tab */}
          <TabsContent value="finance" className="space-y-6">
            {/* Budget Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Budget Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Total Spent */}
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 mb-1">Total Investment</p>
                    <p className="text-3xl font-bold text-green-700">
                      {formatCurrency(reports.budgetSummary.total)}
                    </p>
                    <p className="text-xs text-green-500 mt-1">
                      {reports.depreciation.length} devices with cost data
                    </p>
                  </div>
                  
                  {/* By Year */}
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">Spending by Year</p>
                    <div className="space-y-2">
                      {reports.budgetSummary.byYear.slice(0, 6).map(([year, amount]) => (
                        <div key={year} className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 w-12">{year}</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
                            <div 
                              className="bg-blue-500 h-full rounded-full"
                              style={{ width: `${(amount / reports.budgetSummary.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-24 text-right">{formatCurrency(amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* By Department */}
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">Spending by Department</p>
                    <div className="space-y-2">
                      {reports.budgetSummary.byDepartment.slice(0, 6).map(([dept, amount]) => (
                        <div key={dept} className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 w-20 truncate">{dept}</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
                            <div 
                              className="bg-purple-500 h-full rounded-full"
                              style={{ width: `${(amount / reports.budgetSummary.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-24 text-right">{formatCurrency(amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Asset Depreciation Summary */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Asset Depreciation Summary (5-Year Straight Line)
                </CardTitle>
                {reports.depreciation.length > 0 && (
                  <Button variant="outline" size="sm" onClick={exportDepreciationReport} className="gap-2">
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-sm text-blue-600 mb-1">Original Cost</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalOriginalCost)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-sm text-red-600 mb-1">Accumulated Depreciation</p>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(totalDepreciation)}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-sm text-green-600 mb-1">Current Book Value</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(totalBookValue)}</p>
                  </div>
                </div>

                {reports.depreciation.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No cost data available</p>
                    <p className="text-xs">Add purchase dates and costs to calculate depreciation</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Device</th>
                          <th className="text-left p-3 font-medium text-gray-600">Department</th>
                          <th className="text-left p-3 font-medium text-gray-600">Purchase Date</th>
                          <th className="text-right p-3 font-medium text-gray-600">Original Cost</th>
                          <th className="text-right p-3 font-medium text-gray-600">Age</th>
                          <th className="text-right p-3 font-medium text-gray-600">Depreciation</th>
                          <th className="text-right p-3 font-medium text-gray-600">Book Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {reports.depreciation.slice(0, 20).map(ep => (
                          <tr 
                            key={ep.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/assets/${ep.id}`)}
                          >
                            <td className="p-3">
                              <div className="font-medium">{ep.name}</div>
                              <div className="text-xs text-gray-400">{ep.serial}</div>
                            </td>
                            <td className="p-3 text-gray-600">{ep.meta?.department || '-'}</td>
                            <td className="p-3 text-gray-600">{ep.meta?.purchase_date || '-'}</td>
                            <td className="p-3 text-right font-medium">{formatCurrency(ep.originalCost)}</td>
                            <td className="p-3 text-right text-gray-600">{ep.ageYears}y</td>
                            <td className="p-3 text-right text-red-600">({formatCurrency(ep.accumulatedDepreciation)})</td>
                            <td className="p-3 text-right font-medium text-green-600">{formatCurrency(ep.bookValue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Checkout Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5" />
                  Asset Checkout Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Status Breakdown */}
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                        <UserCheck className="h-8 w-8 text-blue-600" />
                      </div>
                      <p className="text-2xl font-bold">{reports.checkoutStatus.checkedOut}</p>
                      <p className="text-sm text-gray-500">Checked Out</p>
                    </div>
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-2">
                        <UserMinus className="h-8 w-8 text-green-600" />
                      </div>
                      <p className="text-2xl font-bold">{reports.checkoutStatus.available}</p>
                      <p className="text-sm text-gray-500">Available</p>
                    </div>
                  </div>
                  
                  {/* Top Users */}
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-600 mb-3 font-medium">Assets by Assignee</p>
                    {reports.checkoutStatus.byPerson.length === 0 ? (
                      <p className="text-gray-500 text-sm">No checkout data available</p>
                    ) : (
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {reports.checkoutStatus.byPerson.slice(0, 12).map(person => (
                          <div key={person.name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm truncate">{person.name}</span>
                            <Badge variant="outline">{person.count}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Refresh Budget Planning */}
            {reports.needsRefresh.length > 0 && (
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <FileText className="h-5 w-5" />
                    Refresh Budget Planning
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Devices needing replacement by department:</p>
                      <div className="space-y-2">
                        {(() => {
                          const deptCounts = {};
                          reports.needsRefresh.forEach(ep => {
                            const dept = ep.meta?.department || 'Unassigned';
                            deptCounts[dept] = (deptCounts[dept] || 0) + 1;
                          });
                          return Object.entries(deptCounts)
                            .sort((a, b) => b[1] - a[1])
                            .map(([dept, count]) => (
                              <div key={dept} className="flex justify-between items-center p-2 bg-orange-50 rounded">
                                <span className="text-sm">{dept}</span>
                                <Badge className="bg-orange-100 text-orange-700">{count} devices</Badge>
                              </div>
                            ));
                        })()}
                      </div>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <p className="text-sm text-orange-600 mb-2">Estimated Refresh Cost</p>
                      <p className="text-xs text-gray-500 mb-3">
                        Based on average device cost of $1,200
                      </p>
                      <p className="text-3xl font-bold text-orange-700">
                        {formatCurrency(reports.needsRefresh.length * 1200)}
                      </p>
                      <p className="text-xs text-orange-500 mt-2">
                        {reports.needsRefresh.length} devices × $1,200 avg
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AssetReportsPage;
